import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { customerQuizDetailsAction, getOrderDetails } from '../../app/api/useDataManage/userActions';
import DashboardWrapper from './components/DashboardWrapper';
import CustomLink from '../CustomLink';

const PRODUCT_LABELS = {
  riise_formula: 'Riise',
  'omega-3': 'Omega-3',
};

function formatCurrency(value) {
  const numericValue = Number(value || 0);
  return `€${numericValue.toFixed(2)}`;
}

function getOrderItems(order) {
  if (Array.isArray(order?.order_items) && order.order_items.length > 0) {
    return order.order_items;
  }

  if (!order) {
    return [];
  }

  return [
    {
      line_item: 1,
      product_type: order.product_type,
      product_name: order.productName || PRODUCT_LABELS[order.product_type] || 'Product',
      price: order.discounted_price || order.totalPrice || 0,
    },
  ];
}

function InfoRow({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="t-flex t-items-start t-justify-between t-gap-4 t-border-b t-border-gray-200 t-py-3 last:t-border-b-0">
      <span className="t-text-sm t-font-medium t-text-gray-500">{label}</span>
      <span className="t-text-right t-text-sm t-text-gray-900">{value}</span>
    </div>
  );
}

function OrderDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [token] = useState(localStorage.getItem('customer_token'));
  const receivedData = location.state;
  const { quizDetails, orderDetails, isOrderDetailsLoading } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!receivedData?.id) {
      return;
    }

    dispatch(
      getOrderDetails({
        token,
        id: receivedData.id,
      }),
    );
  }, [dispatch, receivedData?.id, token]);

  const resolvedOrder = orderDetails || receivedData || null;
  const orderItems = useMemo(() => getOrderItems(resolvedOrder), [resolvedOrder]);
  const hasRiiseItems = orderItems.some((item) => item.product_type === 'riise_formula');

  useEffect(() => {
    if (!token || !resolvedOrder?.customer_quiz_id || !hasRiiseItems) {
      return;
    }

    dispatch(
      customerQuizDetailsAction({
        token,
        quizData: {
          lang: 'en',
          customer_quiz_id: resolvedOrder.customer_quiz_id,
        },
      }),
    );
  }, [dispatch, hasRiiseItems, resolvedOrder?.customer_quiz_id, token]);

  const displayDate = resolvedOrder?.invoice_date || resolvedOrder?.createdAt;

  return (
    <DashboardWrapper title={t('OrderDetails_Title')}>
      <div className="col-xl-8 col-lg-8 user-inner t-min-w-0 t-w-full">
        <div className="t-flex t-flex-col t-min-w-0">
          <div className="t-flex t-items-center t-justify-between t-gap-4 t-mb-6">
            <div className="welcome">
              <h4 className="t-font-bold">{t('OrderDetails_Heading')}</h4>
            </div>
            <button
              type="button"
              className="t-inline-flex t-items-center t-justify-center t-rounded-full t-bg-secondary t-px-4 t-py-2 t-text-sm t-font-semibold t-text-white t-transition hover:t-opacity-90"
              onClick={() => navigate(-1)}
            >
              {t('OrderDetails_Back')}
            </button>
          </div>

          {isOrderDetailsLoading || !resolvedOrder ? (
            <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6 t-text-sm t-text-gray-500">
              {t('OrderDetails_Loading')}
            </div>
          ) : (
          <div className="t-grid t-gap-4 xl:t-grid-cols-[1.1fr_0.9fr]">
            <div className="t-space-y-4">
              <section className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-4 t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                <div className="t-flex t-items-start t-justify-between t-gap-4">
                  <div>
                    <p className="t-mb-1 t-text-xs t-uppercase t-tracking-[0.16em] t-text-gray-500">
                      {t('OrderDetails_Col_Order')}
                    </p>
                    <h5 className="t-mb-0 t-text-lg t-font-semibold t-text-gray-900">
                      #{resolvedOrder.id || resolvedOrder.orderId}
                    </h5>
                  </div>
                  <span className="t-inline-flex t-rounded-full t-bg-gray-100 t-px-3 t-py-1 t-text-xs t-font-semibold t-capitalize t-text-gray-700">
                    {resolvedOrder.status || '-'}
                  </span>
                </div>

                <div className="t-mt-4 t-grid t-grid-cols-2 t-gap-3">
                  <div className="t-rounded-lg t-bg-gray-50 t-p-3">
                    <p className="t-mb-1 t-text-xs t-uppercase t-tracking-[0.12em] t-text-gray-500">
                      {t('OrderDetails_Col_InvoiceDate')}
                    </p>
                    <p className="t-mb-0 t-text-sm t-font-medium t-text-gray-900">
                      {displayDate ? new Date(displayDate).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <div className="t-rounded-lg t-bg-gray-50 t-p-3">
                    <p className="t-mb-1 t-text-xs t-uppercase t-tracking-[0.12em] t-text-gray-500">
                      {t('OrderDetails_Col_Total')}
                    </p>
                    <p className="t-mb-0 t-text-sm t-font-medium t-text-gray-900">
                      {formatCurrency(resolvedOrder.discounted_price || resolvedOrder.totalPrice)}
                    </p>
                  </div>
                </div>

                <div className="t-mt-4 t-rounded-lg t-border t-border-gray-200 t-p-3">
                  <p className="t-mb-1 t-text-xs t-uppercase t-tracking-[0.12em] t-text-gray-500">
                    {t('CustomerOrders_Col_Tracking')}
                  </p>
                  {resolvedOrder?.details?.includes('https') ? (
                    <CustomLink to={resolvedOrder.details} target="_blank">
                      {t('OrderDetails_TrackingLink')}
                    </CustomLink>
                  ) : (
                    <p className="t-mb-0 t-text-sm t-text-gray-700">
                      {resolvedOrder?.details || t('CustomerOrders_NoTracking')}
                    </p>
                  )}
                </div>
              </section>

              <section className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-4 t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                <div className="t-flex t-items-center t-justify-between t-gap-3 t-mb-3">
                  <h5 className="t-mb-0 t-text-base t-font-semibold t-text-gray-900">
                    {t('OrderDetails_Items')}
                  </h5>
                  <span className="t-text-xs t-text-gray-500">
                    {resolvedOrder.items_count || orderItems.length} {t('CustomerOrders_ItemCountSuffix')}
                  </span>
                </div>

                <div className="t-space-y-3">
                  {orderItems.map((item) => (
                    <div
                      key={`${resolvedOrder.id || resolvedOrder.orderId}-${item.line_item || item.product_type}`}
                      className="t-rounded-lg t-border t-border-gray-200 t-bg-gray-50 t-p-3"
                    >
                      <div className="t-flex t-items-start t-justify-between t-gap-4">
                        <div>
                          <p className="t-mb-1 t-text-sm t-font-semibold t-text-gray-900">
                            {item.product_name || PRODUCT_LABELS[item.product_type] || item.product_type}
                          </p>
                          <p className="t-mb-0 t-text-xs t-uppercase t-tracking-[0.12em] t-text-gray-500">
                            {PRODUCT_LABELS[item.product_type] || item.product_type}
                          </p>
                        </div>
                        <span className="t-text-sm t-font-semibold t-text-gray-900">
                          {formatCurrency(item.price)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {hasRiiseItems ? (
                <section className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-4 t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                  <h5 className="t-mb-3 t-text-base t-font-semibold t-text-gray-900">
                    {t('OrderDetails_Ingredients')}
                  </h5>
                  {quizDetails?.length ? (
                    <div className="t-overflow-hidden t-rounded-lg t-border t-border-gray-200">
                      <div className="t-grid t-grid-cols-[1.6fr_1fr_0.8fr] t-bg-gray-50 t-px-4 t-py-3 t-text-xs t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500">
                        <span>{t('OrderDetails_Col_Nutrient')}</span>
                        <span>{t('OrderDetails_Col_Quantity')}</span>
                        <span>{t('OrderDetails_Col_NRV')}</span>
                      </div>
                      {quizDetails.map((item) => (
                        <div
                          key={item.id || item.ingredient_id}
                          className="t-grid t-grid-cols-[1.6fr_1fr_0.8fr] t-border-t t-border-gray-200 t-px-4 t-py-3 t-text-sm t-text-gray-900"
                        >
                          <span>{item.ingredient}</span>
                          <span>{item.daily_dose} mg</span>
                          <span>{item.per_eu_nrv === 0 ? '*' : `${item.per_eu_nrv}%`}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="t-mb-0 t-text-sm t-text-gray-600">{t('OrderDetails_IngredientsEmpty')}</p>
                  )}
                </section>
              ) : null}
            </div>

            <div className="t-space-y-4">
              {(resolvedOrder.title || resolvedOrder.description) && (
                <section className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-4 t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                  <h5 className="t-mb-2 t-text-base t-font-semibold t-text-gray-900">
                    {t('OrderDetails_SubscriptionInfo')}
                  </h5>
                  <div>
                    <InfoRow label={t('OrderDetails_Label_Title')} value={resolvedOrder.title} />
                    <InfoRow label={t('OrderDetails_Label_Description')} value={resolvedOrder.description} />
                  </div>
                </section>
              )}

              <section className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-4 t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                <h5 className="t-mb-2 t-text-base t-font-semibold t-text-gray-900">
                  {t('OrderDetails_ShippingInfo')}
                </h5>
                <div>
                  <InfoRow label={t('OrderDetails_Label_Address')} value={resolvedOrder.address} />
                  <InfoRow label={t('OrderDetails_Label_Phone')} value={resolvedOrder.phoneNo} />
                  <InfoRow label={t('OrderDetails_Label_State')} value={resolvedOrder.state} />
                  <InfoRow label={t('OrderDetails_Label_ZipCode')} value={resolvedOrder.zipCode} />
                  <InfoRow label={t('OrderDetails_Label_Country')} value={resolvedOrder.country} />
                  <InfoRow label={t('OrderDetails_Label_Email')} value={resolvedOrder.email} />
                </div>
              </section>
            </div>
          </div>
          )}
        </div>
        <div className="blog-detail-inner"></div>
      </div>
    </DashboardWrapper>
  );
}

export default OrderDetails;
